import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Button, Container, Link } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import Body from '../components/Body';
import Image from '../components/Image';
import LoginDialog from '../forms/LoginDialog';
import { useAuth } from '../data/auth/token';
import HideUserByTokenMutation from '../data/mutations/HideUserByTokenMutation';
import UnhideUserByTokenMutation from '../data/mutations/UnhideUserByTokenMutation';

type ViewState = 'initial' | 'visible';

export default function Unhide() {
  const { t } = useTranslation();
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  // The sunset token is a userId or base64-encoded email — a logged-in
  // user's own id works as a token, so fall back to it when the URL has none.
  const token = searchParams.get('token') || auth?.user?._id || '';
  const [view, setView] = useState<ViewState>('initial');
  const [submitting, setSubmitting] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  const [unhideUser] = useMutation(UnhideUserByTokenMutation);
  const [hideUser] = useMutation(HideUserByTokenMutation);

  const performUnhide = async (actionToken: string) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await unhideUser({ variables: { token: actionToken } });
      setView('visible');
    } finally {
      setSubmitting(false);
    }
  };

  const performHideAgain = async (actionToken: string) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await hideUser({ variables: { token: actionToken } });
      setView('initial');
    } finally {
      setSubmitting(false);
    }
  };

  const isVisible = view === 'visible';

  const handleUnhide = () => {
    if (!token) {
      // No token in the URL and not logged in — log in to act on own profile.
      setShowLogin(true);
      return;
    }
    performUnhide(token);
  };

  const handleHideAgain = () => {
    if (!token) {
      setShowLogin(true);
      return;
    }
    performHideAgain(token);
  };
  const titleKey = isVisible ? 'sunset.unhide.confirmed_title' : 'sunset.unhide.initial_title';
  const bodyKey = isVisible ? 'sunset.unhide.confirmed_body' : 'sunset.unhide.initial_body';

  return (
    <>
      <Helmet>
        <title>{t(titleKey)} - Relate</title>
      </Helmet>
      <Body>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Image src="/logo.png" sx={{ width: 65 }} />
        </Box>
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Box sx={{ color: 'white', textAlign: 'center' }}>
            <Typography variant="h2" gutterBottom>
              {t(titleKey)}
            </Typography>
            <Typography variant="body1" sx={{ mb: 5, opacity: 0.9, whiteSpace: 'pre-line' }}>
              {t(bodyKey)}
            </Typography>
            {!isVisible ? (
              <Button
                variant="contained"
                onClick={handleUnhide}
                disabled={submitting}
                size="large"
                sx={{ minWidth: 240, minHeight: 48 }}
              >
                {t('sunset.unhide.initial_cta')}
              </Button>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                <Link
                  component="button"
                  onClick={handleHideAgain}
                  disabled={submitting}
                  sx={{ opacity: 0.7, fontSize: '0.95rem' }}
                >
                  {t('sunset.unhide.hide_again_link')}
                </Link>
              </Box>
            )}
          </Box>
        </Container>
        {showLogin && (
          <LoginDialog
            open
            handleClose={() => setShowLogin(false)}
            onLoggedIn={(user) => {
              setShowLogin(false);
              if (!user?._id) return;
              if (isVisible) {
                performHideAgain(user._id);
              } else {
                performUnhide(user._id);
              }
            }}
          />
        )}
      </Body>
    </>
  );
}
